import { translateLang } from '@/data/i18n/translateLang'
import { matchOriginalPlankknife } from '@/util/LayoutTool'

interface InfluenceItem {
  key: string
  label: string
  value: any
  unit?: string
  isSwitch: boolean
  used: boolean
}

interface InfluenceGroup {
  groupKey: 'params' | 'settings' | 'others'
  groupLabel: string
  items: InfluenceItem[]
}

type InfluenceGroupKey = InfluenceGroup['groupKey']

interface InfluenceCtx {
  ncSetting: any
  preLayoutSetting: any
  preferencesSetting: any
  materialListStore: any
  paibanInfo: any
  paibanData: any[]
  isLessReverse: boolean
  isSpecialShape: boolean
  isSurplusNoRoll: boolean
  activePaibanWay: string[]
  selectedPaibanWay: string
  currentLine: any
}

function boolText(v: boolean): string {
  return v ? translateLang('已开启') : translateLang('关闭')
}

function isSawEnable(ncSetting: any): boolean {
  const s = ncSetting?.sawSetting || {}
  return !!(
    s.mentaoji_enable &&
    s.sawToLayout &&
    s.processType === 'weldingPlank'
  )
}

const GROUP_LABEL_MAP: Record<InfluenceGroupKey, string> = {
  params: translateLang('排版参数'),
  settings: translateLang('排版设置'),
  others: translateLang('其他'),
}

const ITEM_LABEL_MAP: Record<string, string> = {
  plankEdgeOff: translateLang('修边'),
  knifeDiameter: translateLang('下料刀'),
  sawWidthH: translateLang('横切锯片'),
  sawWidthV: translateLang('纵切锯片'),
  layoutGap: translateLang('排版间隙'),
  stackingMethod: translateLang('排版堆叠方式'),
  minSurplusWidthDiff: translateLang('最小余料匹配宽度差'),
  counterWidth: translateLang('台面宽度'),
  sortByHeight: translateLang('按板件长度依次排版'),
  isLessReverse: translateLang('少翻板'),
  isSpecialShape: translateLang('异形嵌套'),
  surplus_no_roll: translateLang('余料不翻板'),
  scattered_layout: translateLang('小板分散排版'),
  sawToLayout: translateLang('锯片厚度排版'),
  surNotLayout: translateLang('余料大板不排指定板件'),
  onlyLayoutTooBig: translateLang('超尺大板只排超尺小板'),
  sameWidthPlank: translateLang('每张大板上只排相同宽度小板'),
  mixPaibanPlans: translateLang('混排方案设置'),
  edgePreMilling: translateLang('封边预铣'),
  multiMaterialKnife: translateLang('多材质下料刀'),
  sawSpecialShapePart: translateLang('锯切雕刻机的异形缺口板'),
  longPlankCutting: translateLang('长板二次加工'),
  specialProcess: translateLang('特殊工艺'),
  surplusCustomTrim: translateLang('余料自定义修边'),
  otherPlateCustomTrim: translateLang('特殊大板单独修边值'),
}

const SWITCH_ITEM_KEYS = new Set([
  'isLessReverse',
  'isSpecialShape',
  'surplus_no_roll',
  'scattered_layout',
  'sawToLayout',
  'surNotLayout',
  'onlyLayoutTooBig',
  'sameWidthPlank',
  'sawSpecialShapePart',
  'longPlankCutting',
  'surplusCustomTrim',
  'otherPlateCustomTrim',
])

function pickKnifeDiameter(ctx: InfluenceCtx): number {
  const isPre = ctx.ncSetting?.isPreLayout
  if (isPre) {
    return (
      ctx.preLayoutSetting?.setting_value?.knife?.diameter ??
      ctx.ncSetting?.knife?.diameter ??
      0
    )
  }
  return ctx.ncSetting?.knife?.diameter ?? 0
}

function pickSortByHeightLabel(v: string): string {
  if (v === 'top') return translateLang('小板在上')
  if (v === 'bottom') return translateLang('小板在下')
  return v || ''
}

function pickStackingLabel(v: string): string {
  if (v === 'long') return translateLang('靠长边排版')
  if (v === 'short') return translateLang('靠短边排版')
  return v || ''
}

/** 从 mentaoji_saws 中取指定方向的最小 saw_width */
function pickSawWidth(
  sawSetting: any,
  direction: 'H' | 'V'
): number | undefined {
  const list = sawSetting?.mentaoji_saws
  if (Array.isArray(list) && list.length) {
    const widths = list
      .filter((it: any) => it?.saw_direction === direction)
      .map((it: any) => Number(it?.saw_width))
      .filter((n) => Number.isFinite(n))
    if (widths.length) return Math.min(...widths)
  }
  if (direction === 'H') {
    const w = Number(sawSetting?.horizontalWidth)
    return Number.isFinite(w) ? w : undefined
  }
  return undefined
}

/** 拉直器槽符号集合（与 LayoutFuncs strentchList 保持一致） */
const STRETCHER_SLOT_SYMBOLS = new Set([
  'STRENTCH',
  'STRENTCHTWO',
  'MIDSTRENTCH',
])

/** 扫描 paibanData 判断"实际用到"的场景 */
function scanPaibanData(paibanData: any[], ncSetting: any) {
  console.log(ncSetting)
  const defaultMargin = ncSetting?.panelSize?.plankEdgeOff
  const plateKnifeMap = ncSetting?.plate_knife_map || {}
  const plateKnifeKeys = Object.keys(plateKnifeMap)
  const matchedPlateKnifeKeys = new Set<string>()
  let hasSurplus = false
  let hasOtherPlate = false
  let surplusCustomTrim = false
  let otherPlateCustomTrim = false
  let maxEdgePreMillingWidth = 0
  let hasSpecialShapePart = false
  let hasStretcherSlot = false
  let hasSawLayout = false
  if (Array.isArray(paibanData)) {
    for (const bp of paibanData) {
      if (bp?.isUseSaw) {
        hasSawLayout = true
      }
      if (bp?.surplusInfo && Object.keys(bp.surplusInfo).length) {
        hasSurplus = true
        const trim = bp.surplusInfo.surplus_trim_edge
        if (trim != null && Number(trim) !== Number(defaultMargin)) {
          surplusCustomTrim = true
        }
      }
      if (bp?.otherPlate && Object.keys(bp.otherPlate).length) {
        hasOtherPlate = true
        const trim = bp.otherPlate.trim_edge
        if (trim != null && Number(trim) !== Number(defaultMargin)) {
          otherPlateCustomTrim = true
        }
      }
      if (Array.isArray(bp?.parts)) {
        for (const part of bp.parts) {
          if (part?.isUseSaw) {
            hasSawLayout = true
          }
          for (const key of plateKnifeKeys) {
            if (matchedPlateKnifeKeys.has(key)) continue
            const matchResult = matchOriginalPlankknife(
              { ...plateKnifeMap[key] },
              part
            )
            if (matchResult?.result) {
              matchedPlateKnifeKeys.add(key)
            }
          }
          const w = Number(part?.edgePreMillingInfo?.width)
          if (Number.isFinite(w) && w > 0 && w > maxEdgePreMillingWidth) {
            maxEdgePreMillingWidth = w
          }
          if (
            Array.isArray(part?.path) &&
            part.path.length > 0 &&
            !part.blueAreaSurplus
          ) {
            hasSpecialShapePart = true
          }
          if (Array.isArray(part?.slots)) {
            for (const slot of part.slots) {
              if (STRETCHER_SLOT_SYMBOLS.has(slot?.symbol)) {
                hasStretcherSlot = true
                break
              }
            }
          }
        }
      }
    }
  }
  return {
    hasSurplus,
    hasOtherPlate,
    surplusCustomTrim,
    otherPlateCustomTrim,
    maxEdgePreMillingWidth,
    hasSpecialShapePart,
    hasStretcherSlot,
    hasSawLayout,
    matchedPlateKnifeCount: matchedPlateKnifeKeys.size,
  }
}

export function buildInfluenceGroups(ctx: InfluenceCtx): InfluenceGroup[] {
  const nc = ctx.ncSetting || {}
  const panelSize = nc.panelSize || {}
  const counterWidth = nc.counter_width ?? 1300
  const sawSetting = nc.sawSetting || {}
  const pref = ctx.preferencesSetting?.setting || {}
  const sawEnable = isSawEnable(nc)
  const scan = scanPaibanData(ctx.paibanData || [], nc)
  const sawLayoutUsed = sawEnable && scan.hasSawLayout
  const horizontalSawWidth = pickSawWidth(sawSetting, 'H')
  const verticalSawWidth = pickSawWidth(sawSetting, 'V')
  const knifeDiameter = pickKnifeDiameter(ctx)
  const mixPaibanPlans = ctx.materialListStore?.selectedMixPaibanPlans || []
  const isPaibanV2 =
    (ctx.selectedPaibanWay || '').replace('_roll', '').indexOf('v2') !== -1

  const longCut = nc.longPlankCuttingSetting || {}
  const longPlankCuttingUsed =
    !!longCut.longPlankCuttingEnable &&
    (!!longCut.cutSingleDoor || !!longCut.cutGeneralPlank)
  const specialProcessLabels: string[] = []
  if (scan.hasStretcherSlot) {
    specialProcessLabels.push(translateLang('拉直器'))
  }
  const hasSpecialProcess = specialProcessLabels.length > 0

  const params: InfluenceItem[] = [
    {
      key: 'plankEdgeOff',
      label: translateLang('修边'),
      value: panelSize.plankEdgeOff ?? 0,
      unit: 'mm',
      isSwitch: false,
      used: true,
    },
    {
      key: 'knifeDiameter',
      label: translateLang('下料刀'),
      value: knifeDiameter,
      unit: 'mm',
      isSwitch: false,
      used: true,
    },
    {
      key: 'sawWidthH',
      label: translateLang('横切锯片'),
      value: horizontalSawWidth,
      unit: 'mm',
      isSwitch: false,
      used: sawLayoutUsed && horizontalSawWidth != null,
    },
    {
      key: 'sawWidthV',
      label: translateLang('纵切锯片'),
      value: verticalSawWidth,
      unit: 'mm',
      isSwitch: false,
      used: sawLayoutUsed && verticalSawWidth != null,
    },
    {
      key: 'layoutGap',
      label: translateLang('排版间隙'),
      value: panelSize.layoutGap ?? 0,
      unit: 'mm',
      isSwitch: false,
      used: true,
    },
    {
      key: 'counterWidth',
      label: translateLang('台面宽度'),
      value: counterWidth ?? 0,
      unit: 'mm',
      isSwitch: false,
      used: nc.counter_limit_able,
    },
    {
      key: 'stackingMethod',
      label: translateLang('排版堆叠方式'),
      value: pickStackingLabel(pref.stacking_method),
      isSwitch: false,
      used: isPaibanV2 && !!pref.stacking_method,
    },
    {
      key: 'minSurplusWidthDiff',
      label: translateLang('最小余料匹配宽度差'),
      value: sawSetting.minSurplusWidthDiff,
      unit: 'mm',
      isSwitch: false,
      used: sawLayoutUsed && sawSetting.minSurplusWidthDiff != null,
    },
    {
      key: 'sortByHeight',
      label: translateLang('按板件长度依次排版'),
      value: pickSortByHeightLabel(sawSetting.sortByHeight),
      isSwitch: false,
      used: sawLayoutUsed && !!sawSetting.sortByHeight,
    },
  ]

  const settings: InfluenceItem[] = [
    {
      key: 'isLessReverse',
      label: translateLang('少翻板'),
      value: boolText(!!ctx.isLessReverse),
      isSwitch: true,
      used: !!ctx.isLessReverse,
    },
    {
      key: 'isSpecialShape',
      label: translateLang('异形嵌套'),
      value: boolText(!!ctx.isSpecialShape),
      isSwitch: true,
      used: !!ctx.isSpecialShape,
    },
    {
      key: 'surplus_no_roll',
      label: translateLang('余料不翻板'),
      value: boolText(!!ctx.isSurplusNoRoll),
      isSwitch: true,
      used: !!ctx.isSurplusNoRoll,
    },
    {
      key: 'scattered_layout',
      label: translateLang('小板分散排版'),
      value: boolText(
        !!nc.movePlankSetting?.scattered_layout || !!nc.scattered_layout
      ),
      isSwitch: true,
      used: !!nc.movePlankSetting?.scattered_layout || !!nc.scattered_layout,
    },
    {
      key: 'sawToLayout',
      label: translateLang('锯片厚度排版'),
      value: boolText(sawLayoutUsed),
      isSwitch: true,
      used: sawLayoutUsed,
    },
    {
      key: 'surNotLayout',
      label: translateLang('余料大板不排指定板件'),
      value: boolText(!!pref.isSurNotLayout),
      isSwitch: true,
      used: !!(pref.isSurNotLayout && pref.isSurNotLayoutMat),
    },
    {
      key: 'onlyLayoutTooBig',
      label: translateLang('超尺大板只排超尺小板'),
      value: boolText(scan.hasOtherPlate),
      isSwitch: true,
      used: scan.hasOtherPlate,
    },
    {
      key: 'sameWidthPlank',
      label: translateLang('每张大板上只排相同宽度小板'),
      value: boolText(!!sawSetting.sameWidthPlank),
      isSwitch: true,
      used: sawLayoutUsed && !!sawSetting.sameWidthPlank,
    },
  ]

  const others: InfluenceItem[] = [
    {
      key: 'mixPaibanPlans',
      label: translateLang('混排方案设置'),
      value: `${translateLang('开启')} (${mixPaibanPlans.length})`,
      isSwitch: false,
      used: mixPaibanPlans.length > 0,
    },
    {
      key: 'edgePreMilling',
      label: translateLang('封边预铣'),
      value: scan.maxEdgePreMillingWidth,
      unit: 'mm',
      isSwitch: false,
      used: scan.maxEdgePreMillingWidth > 0,
    },
    {
      key: 'multiMaterialKnife',
      label: translateLang('多材质下料刀'),
      value: translateLang('开启'),
      isSwitch: false,
      used: scan.matchedPlateKnifeCount > 0,
    },
    {
      key: 'sawSpecialShapePart',
      label: translateLang('锯切雕刻机的异形缺口板'),
      value: boolText(sawLayoutUsed && scan.hasSpecialShapePart),
      isSwitch: true,
      used: sawLayoutUsed && scan.hasSpecialShapePart,
    },
    {
      key: 'longPlankCutting',
      label: translateLang('长板二次加工'),
      value: boolText(longPlankCuttingUsed),
      isSwitch: true,
      used: longPlankCuttingUsed,
    },
    {
      key: 'specialProcess',
      label: translateLang('特殊工艺'),
      value: specialProcessLabels.join('、'),
      isSwitch: false,
      used: hasSpecialProcess,
    },
    {
      key: 'surplusCustomTrim',
      label: translateLang('余料自定义修边'),
      value: boolText(scan.surplusCustomTrim),
      isSwitch: true,
      used: scan.surplusCustomTrim,
    },
    {
      key: 'otherPlateCustomTrim',
      label: translateLang('特殊大板单独修边值'),
      value: boolText(scan.otherPlateCustomTrim),
      isSwitch: true,
      used: scan.otherPlateCustomTrim,
    },
  ]

  return [
    {
      groupKey: 'params',
      groupLabel: translateLang('排版参数'),
      items: params,
    },
    {
      groupKey: 'settings',
      groupLabel: translateLang('排版设置'),
      items: settings,
    },
    {
      groupKey: 'others',
      groupLabel: translateLang('其他可能影响的功能'),
      items: others,
    },
  ]
}

function normalizeGroupKey(key: any): InfluenceGroupKey | null {
  if (key === 'params' || key === 'settings' || key === 'others') return key
  return null
}

/** 历史记录回放：直接使用保存时的 influenceOptions 快照，不再从当前 store 重新计算 */
export function buildInfluenceGroupsFromSnapshot(
  snapshot: any
): InfluenceGroup[] {
  if (!snapshot || !Array.isArray(snapshot.groups)) return []

  return snapshot.groups
    .map((group: any) => {
      const groupKey = normalizeGroupKey(group?.key || group?.groupKey)
      if (!groupKey) return null

      const items = Array.isArray(group.items)
        ? group.items.map((item: any) => {
            const key = String(item?.key || '')
            return {
              key,
              label: item?.label || ITEM_LABEL_MAP[key] || key,
              value: item?.value,
              unit: item?.unit,
              isSwitch:
                typeof item?.isSwitch === 'boolean'
                  ? item.isSwitch
                  : SWITCH_ITEM_KEYS.has(key),
              used: true,
            }
          })
        : []

      return {
        groupKey,
        groupLabel:
          group?.label || group?.groupLabel || GROUP_LABEL_MAP[groupKey],
        items,
      }
    })
    .filter(Boolean) as InfluenceGroup[]
}

/** 生成保存历史用的快照，仅序列化 used===true 的 items */
export function buildInfluenceSnapshot(ctx: InfluenceCtx) {
  const groups = buildInfluenceGroups(ctx)
  return {
    version: 1,
    snapshotAt: new Date().toISOString(),
    groups: groups.map((g) => ({
      key: g.groupKey,
      label: g.groupLabel,
      items: g.items
        .filter((it) => it.used)
        .map((it) => {
          const item: Record<string, any> = {
            key: it.key,
            label: it.label,
            value: it.value,
            isSwitch: it.isSwitch,
          }
          if (it.unit) item.unit = it.unit
          return item
        }),
    })),
  }
}

export type { InfluenceItem, InfluenceGroup, InfluenceCtx }
